import React from 'react';
import { AlertCircle, Clock, CheckCircle2 } from 'lucide-react';

/**
 * SLABadge — compact pill that shows a job's SLA classification.
 * Red = overdue, Amber = at risk, Green = completed/on track. Hidden when status unknown.
 */
export function SLABadge({ status, daysLate, size = 'sm' }) {
  if (!status || status === 'unknown') return null;

  const cfg = {
    overdue: {
      cls: 'bg-rose-50 text-rose-700 border-rose-200',
      Icon: AlertCircle,
      label: daysLate ? `${daysLate}d late` : 'Overdue',
      dot: 'bg-rose-500',
    },
    at_risk: {
      cls: 'bg-amber-50 text-amber-700 border-amber-200',
      Icon: Clock,
      label: 'At Risk',
      dot: 'bg-amber-500',
    },
    on_track: {
      cls: 'bg-emerald-50 text-emerald-700 border-emerald-200',
      Icon: CheckCircle2,
      label: 'On Track',
      dot: 'bg-emerald-500',
    },
    completed: {
      cls: 'bg-slate-100 text-slate-600 border-slate-200',
      Icon: CheckCircle2,
      label: 'Done',
      dot: 'bg-slate-400',
    },
  }[status];

  if (!cfg) return null;
  const { Icon } = cfg;
  const padding = size === 'xs' ? 'px-1.5 py-0.5 text-[10.5px]' : 'px-2 py-0.5 text-[11px]';
  return (
    <span
      className={`inline-flex items-center gap-1 rounded-full border font-medium ${padding} ${cfg.cls}`}
      data-testid={`sla-badge-${status}`}
      title={cfg.label}
    >
      <Icon size={11} />
      {cfg.label}
    </span>
  );
}

/**
 * SLADot — minimal red/amber dot indicator (for tight columns).
 */
export function SLADot({ status }) {
  if (!status || status === 'unknown' || status === 'completed' || status === 'on_track') return null;
  const tone = status === 'overdue' ? 'bg-rose-500 ring-rose-200' : 'bg-amber-500 ring-amber-200';
  return (
    <span
      className={`inline-block w-2 h-2 rounded-full ring-2 ${tone}`}
      data-testid={`sla-dot-${status}`}
      title={status === 'overdue' ? 'Overdue' : 'At Risk'}
    />
  );
}

export default SLABadge;
